import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import services from '../config/services.json';
import genres from '../config/genres.json';
import { tmdb } from '../lib/tmdb.js';
import PosterCard from '../components/PosterCard.jsx';
import MovieModal from '../components/MovieModal.jsx';

const INITIAL_PAGES = 3;   // load 3 pages on first paint = ~60 posters
const PAGE_SIZE     = 20;  // TMDB returns 20 per page

export default function BrowseService() {
  const { serviceId } = useParams();
  const service = useMemo(
    () => services.services.find((s) => s.id === serviceId),
    [serviceId]
  );

  const [genre, setGenre]           = useState('');
  const [yearMin, setYearMin]       = useState('');
  const [yearMax, setYearMax]       = useState('');
  const [runtimeMax, setRuntimeMax] = useState('');
  const [voteMin, setVoteMin]       = useState('');
  const [media, setMedia]           = useState('all'); // 'all' | 'movie' | 'tv'

  const [results, setResults]   = useState([]);
  const [page, setPage]         = useState(0);
  const [totalResults, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [openId, setOpenId]     = useState(null);
  const sentinelRef = useRef(null);

  // Stable filter key — when this changes we reset and reload from page 1.
  const filterKey = `${serviceId}|${media}|${genre}|${yearMin}|${yearMax}|${runtimeMax}|${voteMin}`;

  // Build the param shape we send to /discover. Memoized so we don't rebuild every render.
  const baseParams = useMemo(() => ({
    providers: service ? String(service.tmdbProviderId) : '',
    genre: genre || undefined,
    yearGte: yearMin || undefined,
    yearLte: yearMax || undefined,
    runtimeLte: media === 'movie' ? (runtimeMax || undefined) : undefined,
    voteGte: voteMin || undefined,
    sort: 'popularity.desc',
    media,
  }), [service, media, genre, yearMin, yearMax, runtimeMax, voteMin]);

  // Reset when filters change and load the first chunk.
  useEffect(() => {
    if (!service) return;
    let alive = true;
    setResults([]);
    setPage(0);
    setLoading(true);

    (async () => {
      try {
        // Fire the first N pages in parallel so the initial wall feels full.
        const firstBatch = await Promise.all(
          Array.from({ length: INITIAL_PAGES }, (_, i) =>
            tmdb.discover({ ...baseParams, page: i + 1 })
          )
        );
        if (!alive) return;

        const merged = [];
        const seen = new Set();
        for (const r of firstBatch) {
          for (const m of r.results || []) {
            if (!seen.has(m.id)) { seen.add(m.id); merged.push(m); }
          }
        }

        setResults(merged);
        setPage(INITIAL_PAGES);
        setTotal(firstBatch[0]?.total_results || 0);
        setTotalPages(firstBatch[0]?.total_pages || 0);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [filterKey, baseParams]);

  // Infinite scroll: when sentinel enters viewport, fetch next page and append.
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (page === 0) return;          // initial load not done yet
    if (page >= totalPages) return;  // no more pages
    if (loading) return;             // already loading

    const observer = new IntersectionObserver(async (entries) => {
      const [entry] = entries;
      if (!entry.isIntersecting) return;
      observer.disconnect();

      setLoading(true);
      try {
        const nextPage = page + 1;
        const data = await tmdb.discover({ ...baseParams, page: nextPage });
        setResults((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const additions = (data.results || []).filter((m) => !seen.has(m.id));
          return [...prev, ...additions];
        });
        setPage(nextPage);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, { rootMargin: '600px' }); // start loading well before the user hits the bottom

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page, totalPages, loading, baseParams]);

  if (!service) {
    return (
      <div className="hb-empty">
        We don’t carry that service. <Link to="/">Back to The Floor →</Link>
      </div>
    );
  }

  const hasMore = page < totalPages;

  return (
    <div className="hb-browse">
      <div className="hb-browse__aisle-sign" style={{ background: service.color }}>
        <span className="hb-browse__aisle-label">Now browsing</span>
        <h1 className="hb-browse__aisle-title">{service.name}</h1>
        {totalResults > 0 && (
          <span className="hb-browse__count">
            {results.length.toLocaleString()} of {totalResults.toLocaleString()} titles
          </span>
        )}
      </div>

      <div className="hb-browse__service-switcher">
        {services.services.filter((s) => s.active).map((s) => (
          <Link
            key={s.id}
            to={`/service/${s.id}`}
            className={'hb-chip' + (s.id === service.id ? ' is-active' : '')}
            style={s.id === service.id ? { background: s.color, borderColor: s.color, color: '#fff' } : undefined}
          >
            {s.name}
          </Link>
        ))}
      </div>

      <div className="hb-browse__media-toggle">
        {[
          { id: 'all', label: 'Movies + TV' },
          { id: 'movie', label: 'Movies only' },
          { id: 'tv', label: 'TV shows only' },
        ].map((m) => (
          <button
            key={m.id}
            type="button"
            className={'hb-chip' + (media === m.id ? ' is-active' : '')}
            onClick={() => setMedia(m.id)}
          >{m.label}</button>
        ))}
      </div>

      <div className="hb-browse__filters">
        <label>
          Genre
          <select value={genre} onChange={(e) => setGenre(e.target.value)}>
            <option value="">All</option>
            {genres.aisles.filter((a) => a.tmdbGenreId).map((a) => (
              <option key={a.id} value={a.tmdbGenreId}>{a.name}</option>
            ))}
          </select>
        </label>
        <label>
          Year ≥
          <input type="number" inputMode="numeric" value={yearMin} onChange={(e) => setYearMin(e.target.value)} placeholder="1980" />
        </label>
        <label>
          Year ≤
          <input type="number" inputMode="numeric" value={yearMax} onChange={(e) => setYearMax(e.target.value)} placeholder="2026" />
        </label>
        <label>
          Runtime ≤ (min)
          <input type="number" inputMode="numeric" value={runtimeMax} onChange={(e) => setRuntimeMax(e.target.value)} placeholder="120" />
        </label>
        <label>
          Score ≥
          <input type="number" step="0.1" inputMode="decimal" value={voteMin} onChange={(e) => setVoteMin(e.target.value)} placeholder="7.0" />
        </label>
        {(genre || yearMin || yearMax || runtimeMax || voteMin) && (
          <button
            type="button"
            className="hb-chip"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => { setGenre(''); setYearMin(''); setYearMax(''); setRuntimeMax(''); setVoteMin(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="hb-browse__grid">
        {results.length === 0 && !loading
          ? <div className="hb-empty">No titles match these filters.</div>
          : results.map((m) => (
              <PosterCard key={m.id} movie={m} onClick={() => setOpenId({ tmdb_id: m.id, media_type: m.media_type || 'movie' })} />
            ))
        }
        {loading && results.length === 0 &&
          Array.from({ length: 18 }).map((_, i) => <div key={'sk' + i} className="hb-poster hb-poster--skeleton" />)
        }
      </div>

      {/* Sentinel + loading indicator for infinite scroll */}
      {hasMore && (
        <div ref={sentinelRef} className="hb-browse__sentinel">
          {loading
            ? <span>Loading more titles…</span>
            : <span>Scroll for more</span>}
        </div>
      )}
      {!hasMore && results.length > 0 && (
        <div className="hb-browse__end">
          <span>That’s every title — {results.length.toLocaleString()} in total.</span>
        </div>
      )}

      {openId && <MovieModal movieId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
